import type { GatewayBrowserClient } from "../gateway.ts";

export type NodesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  nodesLoading: boolean;
  nodes: Array<Record<string, unknown>>;
  lastError: string | null;
};

export async function loadNodes(state: NodesState, opts?: { quiet?: boolean }) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.nodesLoading) {
    return;
  }
  state.nodesLoading = true;
  if (!opts?.quiet) {
    state.lastError = null;
  }
  try {
    const res = await state.client.request<{ nodes?: Record<string, unknown> }>("node.list", {});
    state.nodes = Array.isArray(res.nodes) ? res.nodes : [];
  } catch (err) {
    if (!opts?.quiet) {
      state.lastError = String(err);
    }
  } finally {
    state.nodesLoading = false;
  }
}

export type NodeInvokeResult = {
  ok: boolean;
  nodeId?: unknown;
  command?: unknown;
  payload?: unknown;
  payloadJSON?: unknown;
  error?: string;
};

export type NodeInvokeParams = {
  nodeId: string;
  command: string;
  params?: unknown;
  timeoutMs?: number;
  /** Size in bytes for camera_snap; ignored elsewhere. */
  savePath?: string;
};

function genIdempotencyKey(): string {
  // Browser-safe random id. crypto.randomUUID is available in all modern browsers.
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Invoke a node command (e.g. camera_snap, screen_record) via the gateway.
 * Returns the raw gateway result; the caller is responsible for surfacing
 * payload (image bytes, screenshot data, etc.) to the UI.
 */
export async function invokeNodeCommand(
  state: NodesState,
  args: NodeInvokeParams,
): Promise<NodeInvokeResult> {
  if (!state.client || !state.connected) {
    return { ok: false, error: "gateway not connected" };
  }
  if (!args.nodeId || !args.command) {
    return { ok: false, error: "nodeId and command required" };
  }
  try {
    const res = await state.client.request<NodeInvokeResult>("node.invoke", {
      nodeId: args.nodeId,
      command: args.command,
      params: args.params,
      timeoutMs: args.timeoutMs,
      idempotencyKey: genIdempotencyKey(),
    });
    // JAISIU-types-sweep: this was `{ ok: true, ...res }`, but
    // `NodeInvokeResult.ok` is required, so the spread always won and
    // the leading literal was dead weight (TS2783 flagged the
    // duplicate key). `return res` is byte-for-byte the same object —
    // the gateway's own `ok` flag stays authoritative — without
    // pretending to set it here.
    return res;
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Convenience: ask a paired node to snap a camera frame.
 * `facing` is "front" | "back" | "both" (default "front").
 */
export async function snapNodeCamera(
  state: NodesState,
  nodeId: string,
  opts: { facing?: "front" | "back" | "both" } = {},
) {
  return invokeNodeCommand(state, {
    nodeId,
    command: "camera_snap",
    params: { facing: opts.facing ?? "front", format: "jpeg" },
    timeoutMs: 15_000,
  });
}

/**
 * Convenience: start a screen recording on a paired node.
 * `durationMs` defaults to 5000; `screenIndex` defaults to 0.
 */
export async function recordNodeScreen(
  state: NodesState,
  nodeId: string,
  opts: { durationMs?: number; screenIndex?: number; fps?: number } = {},
) {
  return invokeNodeCommand(state, {
    nodeId,
    command: "screen_record",
    params: {
      durationMs: opts.durationMs ?? 5_000,
      screenIndex: opts.screenIndex ?? 0,
      fps: opts.fps ?? 8,
    },
    timeoutMs: 30_000,
  });
}
